import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

import type { RootState } from "../../../store/store";
import { LOGINPAGE } from "../../auth/routes";
import { useGoBack } from "../../../hooks/useGoBack";
import EcoIcon from "../../../components/eco-icon/EcoIcon";
import { SUPERVISOR_HOME_PAGE } from "../routes";
import QuestionField, { QuestionFieldValue } from "../components/QuestionField";

import {
  EvaluationQuestionPublic,
  FreelancerEvaluationPublic,
  UpdateFreelancerEvaluationAnswersRequest,
  updateFreelancerEvaluationAnswersApi,
  submitFreelancerEvaluationApi,
  getFreelancerEvaluationByIdApi,
} from "../../../views/evaluations/freelancerEvaluationsApi";

import "../components/SupFlow.css";
// CSS antigo continua importado: as regras globais dele (`.ListItem:hover`, `.ListItem.checked`, `.BoxMenu`)
// valem para modais antigos de outras telas.
import "./supervisor-freelancer-evaluation.css";

type RouteParams = {
  evaluationId?: string;
};

type DraftAnswer = {
  questionId: string;
  booleanValue?: boolean;
  intValue?: number;
  textValue?: string;
  optionId?: string;
  optionIds?: string[];
};

function formatTimeFromIso(iso?: string) {
  if (!iso) return "-";
  return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

function formatShortDate(iso?: string) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}

function hasValue(q: EvaluationQuestionPublic, a?: DraftAnswer) {
  if (!a) return false;
  if (q.type === "BOOLEAN") return a.booleanValue !== undefined;
  if (q.type === "SCALE") return a.intValue !== undefined;
  if (q.type === "TEXT") return typeof a.textValue === "string" && a.textValue.trim().length > 0;
  if (q.type === "SINGLE_CHOICE") return typeof a.optionId === "string" && a.optionId.length > 0;
  if (q.type === "MULTI_CHOICE") return Array.isArray(a.optionIds) && a.optionIds.length > 0;
  return false;
}

/** Resposta do rascunho no formato do `QuestionField`. */
function valueOf(q: EvaluationQuestionPublic, a?: DraftAnswer): QuestionFieldValue {
  if (!a) return q.type === "MULTI_CHOICE" ? [] : null;
  if (q.type === "BOOLEAN") return a.booleanValue ?? null;
  if (q.type === "SCALE") return a.intValue ?? null;
  if (q.type === "TEXT") return a.textValue ?? null;
  if (q.type === "SINGLE_CHOICE") return a.optionId ?? null;
  if (q.type === "MULTI_CHOICE") return a.optionIds ?? [];
  return null;
}

/** Valor do `QuestionField` como campo do rascunho (`undefined` = sem resposta). */
function patchOf(q: EvaluationQuestionPublic, v: QuestionFieldValue): Partial<DraftAnswer> {
  if (q.type === "BOOLEAN") return { booleanValue: typeof v === "boolean" ? v : undefined };
  if (q.type === "SCALE") return { intValue: typeof v === "number" ? v : undefined };
  if (q.type === "TEXT") return { textValue: typeof v === "string" ? v : undefined };
  if (q.type === "SINGLE_CHOICE") return { optionId: typeof v === "string" && v ? v : undefined };
  if (q.type === "MULTI_CHOICE") return { optionIds: Array.isArray(v) ? v : [] };
  return {};
}

/** Avaliação de freelancer pelo supervisor (redesign de 15/10/2026). */
const SupervisorFreelancerEvaluationPage: React.FC = () => {
  const navigate = useNavigate();
  // Aberta por link direto, volta para a home do supervisor em vez de sair do app.
  const goBack = useGoBack(SUPERVISOR_HOME_PAGE());
  const { evaluationId } = useParams<RouteParams>();

  const token = useSelector((s: RootState) => s.auth.accessToken);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [evaluation, setEvaluation] = useState<FreelancerEvaluationPublic | null>(null);
  const [draft, setDraft] = useState<Record<string, DraftAnswer>>({});
  const [missing, setMissing] = useState<Set<string>>(new Set());

  const submitted = Boolean(evaluation?.submittedAt);

  const questions = useMemo(() => {
    const fromAnswers = (evaluation?.answers ?? [])
      .map((a) => a.question)
      .filter((q): q is EvaluationQuestionPublic => Boolean(q));

    const byId = new Map(fromAnswers.map((q) => [q.id, q]));
    return Array.from(byId.values())
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((q) => ({ ...q, options: [...(q.options ?? [])].sort((x, y) => (x.order ?? 0) - (y.order ?? 0)) }));
  }, [evaluation]);

  const answeredCount = questions.filter((q) => hasValue(q, draft[q.id])).length;

  const load = useCallback(async () => {
    if (!token) {
      toast.error("Você precisa estar logado.");
      navigate(LOGINPAGE(), { replace: true });
      return;
    }

    if (!evaluationId) {
      toast.error("Parâmetros inválidos (evaluationId ausente).");
      goBack();
      return;
    }

    try {
      setLoading(true);

      const ev = await getFreelancerEvaluationByIdApi(decodeURIComponent(evaluationId));

      setEvaluation(ev);

      const byQ: Record<string, DraftAnswer> = {};
      for (const a of ev.answers ?? []) {
        byQ[a.questionId] = {
          questionId: a.questionId,
          booleanValue: a.booleanValue ?? undefined,
          intValue: a.intValue ?? undefined,
          textValue: a.textValue ?? undefined,
          optionId: a.optionId ?? undefined,
          optionIds: a.optionIds ?? undefined,
        };
      }
      setDraft(byQ);
      setMissing(new Set());
    } catch (err: any) {
      toast.error(err?.message ?? "Erro ao carregar avaliação.");
      goBack();
    } finally {
      setLoading(false);
    }
  }, [token, evaluationId, navigate, goBack]);

  useEffect(() => {
    load();
  }, [load]);

  const setAnswer = (questionId: string, patch: Partial<DraftAnswer>) => {
    setDraft((prev) => {
      const base = prev[questionId] ?? { questionId };
      return { ...prev, [questionId]: { ...base, ...patch, questionId } };
    });

    setMissing((prev) => {
      if (!prev.size) return prev;
      const next = new Set(prev);
      next.delete(questionId);
      return next;
    });
  };

  const buildPayload = useCallback((): UpdateFreelancerEvaluationAnswersRequest => {
    const answers: DraftAnswer[] = [];
    for (const q of questions) {
      const a = draft[q.id];
      if (hasValue(q, a)) answers.push(a);
    }
    return { answers };
  }, [questions, draft]);

  const handleSubmit = useCallback(async () => {
    if (!evaluation?.id) return;
    if (submitted) return;

    try {
      setSubmitting(true);
      setMissing(new Set());

      await updateFreelancerEvaluationAnswersApi(evaluation.id, buildPayload());

      const updated = await submitFreelancerEvaluationApi(evaluation.id);
      setEvaluation(updated);

      toast.success("Avaliação enviada!");
      goBack();
    } catch (err: any) {
      const missingIds: string[] | undefined = err?.response?.data?.missingQuestionIds;
      if (Array.isArray(missingIds) && missingIds.length) {
        setMissing(new Set(missingIds));
        toast.error("Preencha as perguntas obrigatórias.");
      } else {
        toast.error(err?.message ?? "Erro ao enviar avaliação.");
      }
    } finally {
      setSubmitting(false);
    }
  }, [evaluation?.id, submitted, buildPayload, goBack]);

  const info = evaluation?.registration;
  const slot = info?.slot;

  return (
    <div className="supFlow">
      <header className="supFlow__band">
        <div className="supFlow__bandInner">
          <div className="supFlow__bandTop">
            <button type="button" className="supFlow__back" onClick={goBack} aria-label="Voltar" disabled={submitting}>
              <EcoIcon name="arrowLeft" size={22} />
            </button>
            <span className="supFlow__topTitle">
              {info?.freelancer?.nomeCompleto ?? "Avaliação de freelancer"}
              <span className="supFlow__topSub">{submitted ? "Avaliação já enviada" : "Avaliação de freelancer"}</span>
            </span>
            <span className="supFlow__spacer" aria-hidden="true" />
          </div>

          <dl className="supFlow__context">
            <div>
              <dt>Loja</dt>
              <dd>{slot?.store?.nomeFantasia ?? "—"}</dd>
            </div>
            <div>
              <dt>Turno</dt>
              <dd>
                {slot?.startAt ? `${formatShortDate(slot.startAt)}, ` : ""}
                {formatTimeFromIso(slot?.startAt)} – {formatTimeFromIso(slot?.endAt)}
              </dd>
            </div>
          </dl>

          {questions.length ? (
            <div className="supFlow__progress">
              <span className="supFlow__progressBar" aria-hidden="true">
                <span
                  className="supFlow__progressFill"
                  style={{ width: `${(answeredCount / questions.length) * 100}%` }}
                />
              </span>
              <span>
                {answeredCount} de {questions.length} respondidas
              </span>
            </div>
          ) : null}
        </div>
      </header>

      <main className="supFlow__main">
        {loading ? (
          <div className="supFlow__empty">Carregando...</div>
        ) : questions.length === 0 ? (
          <div className="supFlow__empty">Nenhuma pergunta encontrada.</div>
        ) : (
          questions.map((q, i) => (
            <QuestionField
              key={q.id}
              index={i + 1}
              question={q}
              value={valueOf(q, draft[q.id])}
              onChange={(v) => setAnswer(q.id, patchOf(q, v))}
              missing={missing.has(q.id)}
              disabled={submitted || submitting}
            />
          ))
        )}
      </main>

      {!submitted && questions.length ? (
        <footer className="supFlow__footer">
          <div className="supFlow__footerInner">
            <button
              type="button"
              className="ecoBtn ecoBtn--primary"
              onClick={handleSubmit}
              disabled={loading || submitting}
            >
              {submitting ? "Enviando..." : "Enviar avaliação"}
            </button>
          </div>
        </footer>
      ) : null}
    </div>
  );
};

export default SupervisorFreelancerEvaluationPage;
